<template>
  <div class="flex items-center justify-between">
    <div>
      <o-tooltip v-if="counter && counter.messages > 1" :text="$t('sms.multipleSMSTooltip')">
        <o-chip variant="info" flat size="sm" square>
          {{ $t('sms.multipleSMS') }}
          <o-icon icon="info" class="inline-block ml-3" />
        </o-chip>
      </o-tooltip>
    </div>
    <div class="flex items-center space-x-4">
      <div>
        <o-tooltip :text="$t('sms.specialTooltip')">
          <o-chip v-if="counter && counter.special" variant="warning" flat size="sm" square>
            {{ $t('sms.special', { special: counter.special }) }}
          </o-chip>
        </o-tooltip>
      </div>
      <div class="text-sm font-semibold text-gray-700">
        <o-tooltip>
          <o-chip icon="info">
            <template v-if="counter">
              {{ counter.remaining }} / {{ counter.messages }}
            </template>
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <template v-else>
              160 / 0
            </template>
          </o-chip>

          <template #content>
            {{ $t('sms.remainingMessages', { remaining: counter.remaining, messages: counter.messages }) }}
          </template>
        </o-tooltip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  value?: string
}

const props = withDefaults(defineProps<Props>(), {
  value: '',
})

const gsm7bitChars = '@£$¥èéùìòÇ\\nØø\\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\\"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
const gsm7bitExChar = '\\^{}\\\\\\[~\\]|€'

const gsm7bitRegExp = RegExp('^[' + gsm7bitChars + ']*$')
const gsm7bitExRegExp = RegExp('^[' + gsm7bitChars + gsm7bitExChar + ']*$')
const gsm7bitExRegExpRev = RegExp('[' + gsm7bitChars + gsm7bitExChar + ']+', 'g')
const gsm7bitExOnlyRegExp = RegExp('^[\\' + gsm7bitExChar + ']*$')

const counter = ref<any>(null)

const detectEncoding = (text: string): string => {
  switch (false) {
    case text.match(gsm7bitRegExp) == null:
      return 'GSM_7BIT'
    case text.match(gsm7bitExRegExp) == null:
      return 'GSM_7BIT_EX'
    default:
      return 'UTF16'
  }
}

const countGsm7bitEx = (text: string): number => {
  let char2
  const chars = (() => {
    let _i, _len
    const _results: string[] = []
    for (_i = 0, _len = text.length; _i < _len; _i++) {
      char2 = text[_i]
      if (char2.match(gsm7bitExOnlyRegExp) != null) {
        _results.push(char2)
      }
    }
    return _results
  })()
  return chars.length
}

const count = () => {
  const text = props.value.trim()
  const encoding = detectEncoding(text)
  let special

  const messageLength: Record<string, number> = {
    GSM_7BIT: 160,
    GSM_7BIT_EX: 160,
    UTF16: 70,
  }

  const multiMessageLength: Record<string, number> = {
    GSM_7BIT: 153,
    GSM_7BIT_EX: 153,
    UTF16: 67,
  }

  let length = text.length
  if (encoding === 'GSM_7BIT_EX') {
    length += countGsm7bitEx(text)
  }

  if (encoding === 'UTF16') {
    const unmatched = text.replace(gsm7bitExRegExpRev, '').replace(/\s/g, '')
    const unique = [...new Set(unmatched)]
    special = unique.join(', ')
  }

  let per_message = messageLength[encoding]
  if (length > per_message) {
    per_message = multiMessageLength[encoding]
  }

  const messages = Math.ceil(length / per_message)

  let remaining = (per_message * messages) - length
  if (remaining === 0 && messages === 0) {
    remaining = per_message
  }

  return {
    encoding,
    length,
    per_message,
    remaining,
    messages,
    special,
  }
}

watch(() => props.value, () => {
  counter.value = count()
}, { immediate: true })
</script>
